import { useEffect, useRef, useState } from "react";
import { QRCodeSVG } from "qrcode.react";
import { toast } from "sonner";
import api, { formatErr } from "../../lib/api";
import { useOfficeFilter } from "../../lib/officeFilter";
import { useRealtimeEvent } from "../../lib/realtime";
import Portal from "../Portal";
import AppleModal, { AppleField } from "../AppleModal";
import DatePicker from "../DatePicker";
import {
  X,
  Camera,
  Check,
  Building2,
  QrCode,
  ScanLine,
  User as UserIcon,
} from "@/components/icons";

const FRONTEND = window.location.origin;

const empty = {
  full_name: "",
  document_id: "",
  birth_date: "",
  gender: "",
  phone: "",
  email: "",
  address: "",
  blood_type: "",
  photo_url: "",
  allergies: "",
  notes: "",
  guardian_role: "",
  guardian_name: "",
  guardian_phone: "",
};

const GUARDIAN_ROLES = [
  { value: "", label: "—" },
  { value: "padre", label: "Padre" },
  { value: "madre", label: "Madre" },
  { value: "tutor", label: "Tutor" },
  { value: "tutora", label: "Tutora" },
  { value: "otro", label: "Otro" },
];

const initials = (name) =>
  (name || "")
    .split(" ")
    .map((s) => s[0])
    .filter(Boolean)
    .slice(0, 2)
    .join("")
    .toUpperCase();

export default function NewPatientModal({ open, onClose, onCreated }) {
  const { officeId } = useOfficeFilter();
  const [offices, setOffices] = useState([]);
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);
  const [success, setSuccess] = useState(false);
  const [scanSession, setScanSession] = useState(null); // { token, expiresAt }
  const [scanTicking, setScanTicking] = useState("");
  const fileRef = useRef(null);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  // Reset y cargar oficinas al abrir
  useEffect(() => {
    if (!open) return;
    setForm(empty);
    setSaving(false);
    setSuccess(false);
    setScanSession(null);
    (async () => {
      try {
        const { data } = await api.get("/offices");
        setOffices(data || []);
      } catch {
        setOffices([]);
      }
      // crear sesión de scan al abrir
      startScanSession();
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // ESC para cerrar
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === "Escape" && !saving) onClose?.();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, saving, onClose]);

  // Contador de expiración del QR
  useEffect(() => {
    if (!scanSession?.expiresAt) return;
    const tick = () => {
      const ms = new Date(scanSession.expiresAt).getTime() - Date.now();
      if (ms <= 0) {
        setScanTicking("Expirado");
        return;
      }
      const s = Math.floor(ms / 1000);
      const mm = Math.floor(s / 60);
      const ss = s % 60;
      setScanTicking(`${mm}:${String(ss).padStart(2, "0")}`);
    };
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [scanSession?.expiresAt]);

  // Cuando el paciente/tutor termina de escanear
  useRealtimeEvent("scan:updated", (payload) => {
    if (!scanSession || payload.token !== scanSession.token) return;
    if (payload.status === "failed") {
      toast.error("No pudimos leer la foto. Pídele que la retome.");
      return;
    }
    if (payload.status !== "extracted") return;
    const ex = payload.extracted || {};
    setForm((f) => ({
      ...f,
      full_name: ex.full_name || f.full_name,
      document_id: ex.cedula || f.document_id,
      birth_date: ex.birth_date || f.birth_date,
      gender: ex.gender || f.gender,
      phone: ex.phone || f.phone,
      address: ex.address || f.address,
      blood_type: ex.blood_type || f.blood_type,
    }));
    toast.success("Datos extraídos del documento ✓");
    // Renovar sesión por si quiere volver a escanear
    startScanSession();
  });

  const startScanSession = async () => {
    try {
      const { data } = await api.post("/patient-scan/session");
      setScanSession({
        token: data.token,
        expiresAt: data.expires_at,
      });
    } catch {
      setScanSession(null);
    }
  };

  const onPickPhoto = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 800_000) {
      toast.error("La imagen es muy grande (máx 800KB)");
      return;
    }
    const reader = new FileReader();
    reader.onload = () => set("photo_url", reader.result);
    reader.readAsDataURL(f);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.full_name.trim()) {
      toast.error("El nombre es obligatorio");
      return;
    }
    setSaving(true);
    try {
      const resolvedOfficeId =
        officeId || (offices.length === 1 ? offices[0].id : null);
      const guardians =
        form.guardian_role && form.guardian_name
          ? [
              {
                role: form.guardian_role,
                full_name: form.guardian_name,
                phone: form.guardian_phone || null,
              },
            ]
          : [];
      const { data } = await api.post("/patients", {
        full_name: form.full_name,
        document_id: form.document_id || null,
        birth_date: form.birth_date || null,
        gender: form.gender || null,
        phone: form.phone || null,
        email: form.email || null,
        address: form.address || null,
        blood_type: form.blood_type || null,
        photo_url: form.photo_url || null,
        allergies: form.allergies || null,
        notes: form.notes || null,
        guardians,
        office_id: resolvedOfficeId,
      });
      setSuccess(true);
      setTimeout(() => {
        onCreated?.(data);
        onClose?.();
      }, 1400);
    } catch (err) {
      toast.error(formatErr(err.response?.data?.detail));
      setSaving(false);
    }
  };

  if (!open) return null;

  const activeOffice = offices.find((o) => o.id === officeId);
  const scanUrl = scanSession ? `${FRONTEND}/scan/${scanSession.token}` : "";

  // SUCCESS: overlay minimalista — oculta todo el formulario y muestra sólo
  // la palomita animada.
  if (success) {
    return (
      <Portal>
        <div
          className="fixed inset-0 z-[80] flex items-center justify-center p-4 bg-black/20 backdrop-blur-md"
          data-testid="patient-success-overlay"
        >
          <div className="flex flex-col items-center gap-4 animate-in fade-in zoom-in duration-300">
            <SuccessCheck />
            <div className="text-center">
              <div className="text-[18px] font-semibold text-white drop-shadow">
                Paciente creado
              </div>
              {form.full_name && (
                <div className="text-[13px] text-white/80 mt-1 drop-shadow">
                  {form.full_name}
                </div>
              )}
            </div>
          </div>
        </div>
      </Portal>
    );
  }

  return (
    <AppleModal
      open
      onClose={saving ? undefined : onClose}
      icon={<UserIcon weight="regular" className="w-8 h-8" strokeWidth={1.5} />}
      title="Nuevo paciente"
      subtitle="Completa los datos o escanea el documento con el QR"
      size="xl"
      compact
      testid="new-patient-modal"
    >
      <div className="relative">
        <form onSubmit={submit} className="grid grid-cols-1 md:grid-cols-[1fr_220px] gap-4 max-h-[62vh] overflow-y-auto no-scrollbar pr-2">
            {/* LEFT: form */}
            <div className="space-y-3">
              {/* Foto + Nombre */}
              <div className="flex items-center gap-3">
                <div className="relative">
                  {form.photo_url ? (
                    <img
                      src={form.photo_url}
                      alt=""
                      className="w-14 h-14 rounded-full object-cover"
                    />
                  ) : (
                    <div className="w-14 h-14 rounded-full bg-[#F2F3F5] flex items-center justify-center text-[14px] font-bold text-black">
                      {initials(form.full_name) || <UserIcon className="w-5 h-5 text-black/40" strokeWidth={1.75} weight="regular" />}
                    </div>
                  )}
                  <button
                    type="button"
                    onClick={() => fileRef.current?.click()}
                    className="absolute -bottom-1 -right-1 w-6 h-6 rounded-full bg-black text-white flex items-center justify-center shadow"
                    data-testid="new-patient-photo-button"
                    title="Subir foto"
                  >
                    <Camera className="w-3 h-3" strokeWidth={1.75} weight="regular" />
                  </button>
                  <input
                    ref={fileRef}
                    type="file"
                    accept="image/*"
                    className="hidden"
                    onChange={onPickPhoto}
                  />
                </div>
                <div className="flex-1 min-w-0">
                  <AppleField
                    label="Nombre completo"
                    value={form.full_name}
                    onChange={(v) => set("full_name", v)}
                    placeholder="Ej: María González"
                    testid="patient-full-name"
                    valid={form.full_name?.trim().length > 2}
                    autoFocus
                  />
                </div>
              </div>

              {/* Oficina activa */}
              {activeOffice && (
                <div className="flex items-center gap-2 rounded-lg bg-[#F2F3F5] px-2.5 py-1.5">
                  <div className="w-7 h-7 rounded-md bg-black text-white flex items-center justify-center flex-shrink-0">
                    <Building2 className="w-3.5 h-3.5" strokeWidth={1.75} weight="regular" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="text-[9px] uppercase tracking-widest font-semibold text-black/45">
                      Oficina asignada
                    </div>
                    <div className="font-semibold text-black text-[12px] truncate">
                      {activeOffice.name}
                    </div>
                  </div>
                </div>
              )}

              {/* Campos personales */}
              <div className="grid grid-cols-2 gap-2.5">
                <AppleField
                  label="Documento"
                  value={form.document_id}
                  onChange={(v) => set("document_id", v)}
                  placeholder="Cédula o pasaporte"
                  testid="patient-document"
                  valid={form.document_id?.length >= 5}
                />
                <Field label="Fecha de nacimiento">
                  <DatePicker
                    value={form.birth_date}
                    onChange={(v) => set("birth_date", v)}
                    testid="patient-birth-date"
                  />
                </Field>
                <AppleField
                  label="Teléfono"
                  value={form.phone}
                  onChange={(v) => set("phone", v)}
                  placeholder="809 555 1234"
                  testid="patient-phone"
                  valid={form.phone?.length >= 8}
                />
                <AppleField
                  label="Correo"
                  type="email"
                  value={form.email}
                  onChange={(v) => set("email", v)}
                  placeholder="paciente@correo.com"
                  testid="patient-email"
                  valid={/^[^@]+@[^@]+\.[^@]+$/.test(form.email || "")}
                />
                <Field label="Género">
                  <select
                    value={form.gender}
                    onChange={(e) => set("gender", e.target.value)}
                    className="w-full h-9 rounded-lg border border-black/[0.08] bg-white px-3 text-[12px] focus:outline-none focus:border-black/40 transition"
                    data-testid="patient-gender"
                  >
                    <option value="">—</option>
                    <option value="F">Femenino</option>
                    <option value="M">Masculino</option>
                    <option value="O">Otro</option>
                  </select>
                </Field>
                <Field label="Tipo de sangre">
                  <select
                    value={form.blood_type}
                    onChange={(e) => set("blood_type", e.target.value)}
                    className="w-full h-9 rounded-lg border border-black/[0.08] bg-white px-3 text-[12px] focus:outline-none focus:border-black/40 transition"
                    data-testid="patient-blood-type"
                  >
                    <option value="">—</option>
                    {["O+","O-","A+","A-","B+","B-","AB+","AB-"].map((bt) => (
                      <option key={bt} value={bt}>{bt}</option>
                    ))}
                  </select>
                </Field>
              </div>

              <Field label="Dirección">
                <input
                  value={form.address}
                  onChange={(e) => set("address", e.target.value)}
                  placeholder="Calle, sector, ciudad"
                  className="w-full h-9 rounded-lg border border-black/[0.08] bg-white px-3 text-[12px] focus:outline-none focus:border-black/40 transition"
                  data-testid="patient-address"
                />
              </Field>

              {/* Responsable / Tutor */}
              <div className="pt-2 border-t border-black/[0.05]">
                <div className="text-[9px] uppercase tracking-widest font-semibold text-black/45 mb-1.5">
                  Responsable / Tutor
                </div>
                <div className="grid grid-cols-[100px_1fr_120px] gap-2">
                  <select
                    value={form.guardian_role}
                    onChange={(e) => set("guardian_role", e.target.value)}
                    className="h-9 rounded-lg border border-black/[0.08] bg-white px-2 text-[12px] focus:outline-none focus:border-black/40 transition"
                    data-testid="patient-guardian-role"
                  >
                    {GUARDIAN_ROLES.map((r) => (
                      <option key={r.value} value={r.value}>
                        {r.label}
                      </option>
                    ))}
                  </select>
                  <input
                    value={form.guardian_name}
                    onChange={(e) => set("guardian_name", e.target.value)}
                    placeholder="Nombre completo del responsable"
                    className="h-9 rounded-lg border border-black/[0.08] bg-white px-3 text-[12px] focus:outline-none focus:border-black/40 transition"
                    data-testid="patient-guardian-name"
                  />
                  <input
                    value={form.guardian_phone}
                    onChange={(e) => set("guardian_phone", e.target.value)}
                    placeholder="Teléfono"
                    className="h-9 rounded-lg border border-black/[0.08] bg-white px-3 text-[12px] focus:outline-none focus:border-black/40 transition"
                    data-testid="patient-guardian-phone"
                  />
                </div>
              </div>

              {/* Historial médico */}
              <div className="pt-2 border-t border-black/[0.05]">
                <div className="text-[9px] uppercase tracking-widest font-semibold text-black/45 mb-1.5">
                  Historial médico
                </div>
                <div className="grid grid-cols-1 gap-2">
                  <Field label="Alergias">
                    <input
                      value={form.allergies}
                      onChange={(e) => set("allergies", e.target.value)}
                      placeholder="Penicilina, mariscos…"
                      className="w-full h-9 rounded-lg border border-black/[0.08] bg-white px-3 text-[12px] focus:outline-none focus:border-black/40 transition"
                      data-testid="patient-allergies"
                    />
                  </Field>
                  <Field label="Historial de enfermedades / condiciones previas">
                    <textarea
                      value={form.notes}
                      onChange={(e) => set("notes", e.target.value)}
                      placeholder="Hipertensión, diabetes, cirugías previas, medicación actual…"
                      className="w-full min-h-[56px] rounded-lg border border-black/[0.08] bg-white px-3 py-2 text-[12px] focus:outline-none focus:border-black/40 transition"
                      data-testid="patient-notes"
                    />
                  </Field>
                </div>
              </div>
            </div>

            {/* RIGHT: QR panel */}
            <div className="border-t md:border-t-0 md:border-l border-black/[0.05] md:pl-4 pt-3 md:pt-0 flex flex-col">
              <div className="flex items-center gap-1.5 text-[12px] font-semibold text-black">
                <QrCode className="w-3.5 h-3.5 text-black/70" strokeWidth={1.75} weight="regular" />
                Escanear con QR
              </div>
              <div className="text-[10.5px] text-black/50 mt-1 leading-snug">
                Para escanear la foto o documento, apunta con la cámara del paciente al código QR.
              </div>

              <div className="mt-2.5 flex-1 flex flex-col items-center justify-start">
                <div className="w-[170px] h-[170px] rounded-xl bg-white border border-black/[0.08] p-2.5 flex items-center justify-center">
                  {scanUrl ? (
                    <QRCodeSVG
                      value={scanUrl}
                      size={148}
                      level="M"
                      bgColor="#ffffff"
                      fgColor="#000000"
                    />
                  ) : (
                    <div className="text-[11px] text-black/40 text-center">
                      Generando código…
                    </div>
                  )}
                </div>
                <div className="mt-1.5 text-[10px] text-black/45 flex items-center gap-1">
                  <ScanLine className="w-3 h-3" strokeWidth={1.75} weight="regular" />
                  Válido por {scanTicking || "10:00"}
                </div>
                <button
                  type="button"
                  onClick={startScanSession}
                  className="mt-2 h-7 px-2.5 rounded-md bg-[#F2F3F5] hover:bg-black/[0.08] text-[10.5px] font-semibold text-black/70 transition"
                  data-testid="new-patient-regenerate-qr"
                >
                  Generar nuevo código
                </button>
              </div>
            </div>

            {/* Footer full-width */}
            <div className="md:col-span-2 flex items-center justify-between gap-2 pt-2.5 border-t border-black/[0.05]">
              <div className="text-[10.5px] text-black/40 leading-snug">
                Al escanear con el QR, la IA rellena automáticamente los campos.
              </div>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={onClose}
                  disabled={saving}
                  className="h-9 px-3.5 rounded-lg bg-white border border-black/10 hover:bg-black/[0.04] text-[12.5px] font-semibold text-black transition disabled:opacity-50"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  disabled={saving}
                  data-testid="new-patient-submit"
                  className="h-10 px-5 rounded-full bg-[#0071E3] text-white hover:bg-[#0077ED] text-[13px] font-semibold transition disabled:opacity-40"
                >
                  {saving ? "Guardando…" : "Crear paciente"}
                </button>
              </div>
            </div>
          </form>
      </div>

      <style>{`
        @keyframes eme-circle-draw {
          0% { stroke-dashoffset: 200; }
          100% { stroke-dashoffset: 0; }
        }
        @keyframes eme-check-draw {
          0% { stroke-dashoffset: 60; }
          100% { stroke-dashoffset: 0; }
        }
        @keyframes eme-pop {
          0% { transform: scale(0.7); opacity: 0; }
          60% { transform: scale(1.08); opacity: 1; }
          100% { transform: scale(1); opacity: 1; }
        }
      `}</style>
    </AppleModal>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <label className="text-[10px] uppercase tracking-widest font-semibold text-black/45">
        {label}
      </label>
      <div className="mt-1">{children}</div>
    </div>
  );
}

function SuccessCheck() {
  return (
    <div
      className="w-24 h-24 rounded-full bg-emerald-50 flex items-center justify-center"
      style={{ animation: "eme-pop 400ms cubic-bezier(0.34,1.56,0.64,1) both" }}
    >
      <svg viewBox="0 0 60 60" className="w-16 h-16">
        <circle
          cx="30"
          cy="30"
          r="26"
          fill="none"
          stroke="#10B981"
          strokeWidth="3"
          strokeLinecap="round"
          style={{
            strokeDasharray: 200,
            strokeDashoffset: 200,
            animation: "eme-circle-draw 500ms ease-out 100ms forwards",
          }}
        />
        <path
          d="M18 31 L27 40 L43 22"
          fill="none"
          stroke="#10B981"
          strokeWidth="4"
          strokeLinecap="round"
          strokeLinejoin="round"
          style={{
            strokeDasharray: 60,
            strokeDashoffset: 60,
            animation: "eme-check-draw 350ms ease-out 550ms forwards",
          }}
        />
      </svg>
    </div>
  );
}
